/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MigrationJob } from './migration.models';

/**
 * The contract with the migration engine.
 *
 * The engine will live in the `aca-dms-jobs` sidecar under `/api/migration/*`
 * — same service, same base URL (`jobs.baseUrl` in app.config.json, proxied
 * as `/jobs-api` in dev). Until those endpoints exist every call fails and
 * the UI reports "the migration service is not configured" instead of
 * pretending a migration ran.
 *
 * Nothing in here ever returns a synthetic success: `health()` answers null
 * whenever the service is unreachable, and every screen keys its executable
 * actions off that answer.
 */
@Injectable({ providedIn: 'root' })
export class MigrationApiService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);

  /** Same sidecar as the scheduled jobs — /api/migration sits beside /api/jobs. */
  private get baseUrl(): string {
    return this.appConfig.get<string>('jobs.baseUrl', '/jobs-api');
  }

  /**
   * The last known engine state — null until the first probe answers. Every
   * screen keys its executable actions off this one signal rather than
   * probing on its own.
   */
  readonly available = signal<boolean | null>(null);

  /**
   * Connector ids the running engine actually implements — published by its
   * health answer. The registry marks more connectors selectable than every
   * engine build supports; this list is the honest subset. Empty until the
   * first probe answers.
   */
  readonly engineConnectors = signal<string[]>([]);

  private reprobeTimer: ReturnType<typeof setInterval> | null = null;

  /** Re-checks the engine; safe to call on every visit to the center. While the
   * engine is down it keeps re-probing — a sidecar that comes up after the page
   * opened must not need a manual refresh. The retry stops the moment health answers. */
  probe(): void {
    this.health().subscribe((res) => {
      const up = res !== null;
      this.available.set(up);
      this.engineConnectors.set(res?.connectors ?? []);
      if (up) {
        if (this.reprobeTimer !== null) {
          clearInterval(this.reprobeTimer);
          this.reprobeTimer = null;
        }
      } else if (this.reprobeTimer === null) {
        this.reprobeTimer = setInterval(() => this.probe(), 15_000);
      }
    });
  }

  /**
   * What the engine answers about itself, or null when it is not there.
   * `engine: false` means the sidecar is up but the migration module is not
   * built into it — still "not configured" for UI purposes.
   */
  health(): Observable<{ status: string; engine?: boolean; version?: string; connectors?: string[] } | null> {
    return this.http
      .get<{ status: string; engine?: boolean; version?: string; connectors?: string[] }>(`${this.baseUrl}/api/migration/health`, {
        headers: this.credentials()
      })
      .pipe(
        map((res) => (res && res.status === 'ready' && res.engine !== false ? res : null)),
        catchError(() => of(null))
      );
  }

  /** True when the running engine says it implements this connector. */
  engineSupports(connectorId: string): boolean {
    return this.available() === true && this.engineConnectors().includes(connectorId);
  }

  /**
   * Server-side connection test — the only honest place for it. Takes the
   * connector id plus the full form values (secrets included); the service
   * must not persist or return the secret fields. When `connectionId` is
   * given, a successful test also registers the config in the engine's vault
   * so runs can use the secrets later.
   */
  testConnection(
    connectorId: string,
    config: Record<string, unknown>,
    connectionId?: string
  ): Observable<{ ok: boolean; detail?: string; server?: any }> {
    return this.http.post<{ ok: boolean; detail?: string; server?: any }>(
      `${this.baseUrl}/api/migration/connections/test`,
      { connectorId, config, connectionId },
      { headers: this.credentials() }
    );
  }

  /**
   * Hands a connection's full config — secrets included — to the engine's
   * encrypted vault. Called on save when the form carried secret values; the
   * repository copy still never sees them.
   */
  registerSecrets(connectionId: string, connectorId: string, config: Record<string, unknown>): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(
      `${this.baseUrl}/api/migration/connections/register`,
      { connectionId, connectorId, config },
      { headers: this.credentials() }
    );
  }

  /** Drops a deleted connection's vaulted secrets. */
  forgetConnection(connectionId: string): Observable<{ removed: boolean }> {
    return this.http.post<{ removed: boolean }>(`${this.baseUrl}/api/migration/connections/${encodeURIComponent(connectionId)}/forget`, null, {
      headers: this.credentials()
    });
  }

  /** Scans the source through a connection — connector-defined result shape. */
  discover(connectionId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/migration/discover`, { connectionId }, { headers: this.credentials() });
  }

  /** One level of the source tree for the selection browser. */
  browse(connectionId: string, path: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/migration/browse`, { connectionId, path }, { headers: this.credentials() });
  }

  /** Server-side validation of a job definition before it may run. */
  validate(job: MigrationJob): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/migration/validate`, job, { headers: this.credentials() });
  }

  /** A rehearsal that must not write to the destination. */
  dryRun(job: MigrationJob): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/migration/dryrun`, job, { headers: this.credentials() });
  }

  /** Starts a saved job. The job id is the definition file's node id. */
  start(jobId: string): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/api/migration/jobs/${encodeURIComponent(jobId)}/start`, null, { headers: this.credentials() })
      .pipe(map(() => true));
  }

  pause(jobId: string): Observable<boolean> {
    return this.control(jobId, 'pause');
  }

  resume(jobId: string): Observable<boolean> {
    return this.control(jobId, 'resume');
  }

  cancel(jobId: string): Observable<boolean> {
    return this.control(jobId, 'cancel');
  }

  /** Retries the failed items of a run — never the whole job again. */
  retry(jobId: string, itemIds: string[]): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/api/migration/jobs/${encodeURIComponent(jobId)}/retry`, { items: itemIds }, { headers: this.credentials() })
      .pipe(map(() => true));
  }

  reconcile(jobId: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/api/migration/jobs/${encodeURIComponent(jobId)}/reconcile`, null, {
      headers: this.credentials()
    });
  }

  /** Live state of a run this engine is tracking — null when no run is live. */
  jobState(jobId: string): Observable<{
    jobId: string;
    status: string;
    startedAt?: string;
    finishedAt?: string;
    totalItems?: number;
    statistics?: any;
    errors?: number;
    error?: string;
  } | null> {
    return this.http
      .get<any>(`${this.baseUrl}/api/migration/jobs/${encodeURIComponent(jobId)}`, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  private control(jobId: string, op: string): Observable<boolean> {
    return this.http
      .post(`${this.baseUrl}/api/migration/jobs/${encodeURIComponent(jobId)}/${op}`, null, { headers: this.credentials() })
      .pipe(map(() => true));
  }

  /** The engine calls Alfresco as the administrator — a ticket goes along. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
